<template>
  <div
    ref="honorLoop"
    class="honor-loop"
    tabindex="0"
    :aria-label="ariaLabel"
    @mouseenter="pause('hover')"
    @mouseleave="resume('hover')"
    @focusin="pause('focus')"
    @focusout="resume('focus')"
    @pointerdown="pause('pointer')"
    @pointerup="resume('pointer')"
    @pointercancel="resume('pointer')"
  >
    <div ref="honorGroup" class="honor-loop-group">
      <p v-for="item in items" :key="item">
        <span aria-hidden="true">✦</span>
        {{ item }}
      </p>
    </div>
    <div class="honor-loop-group" aria-hidden="true">
      <p v-for="item in items" :key="`duplicate-${item}`">
        <span aria-hidden="true">✦</span>
        {{ item }}
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
  interface Props {
    ariaLabel?: string
    items: readonly string[]
    speed?: number
  }

  const props = withDefaults(defineProps<Props>(), {
    ariaLabel: '循环列表',
    speed: 0.35,
  })

  const honorLoop = useTemplateRef<HTMLElement>('honorLoop')
  const honorGroup = useTemplateRef<HTMLElement>('honorGroup')
  const pauseReasons = new Set<string>()

  let animationFrame = 0
  let isVisible = false
  let observer: IntersectionObserver | undefined
  let reducedMotion: MediaQueryList | undefined

  function stop() {
    if (!animationFrame) return
    window.cancelAnimationFrame(animationFrame)
    animationFrame = 0
  }

  function start() {
    if (
      animationFrame ||
      !isVisible ||
      pauseReasons.size > 0 ||
      reducedMotion?.matches ||
      document.hidden
    ) {
      return
    }

    const tick = () => {
      const loop = honorLoop.value
      const group = honorGroup.value
      if (
        !loop ||
        !group ||
        !isVisible ||
        pauseReasons.size > 0 ||
        reducedMotion?.matches ||
        document.hidden
      ) {
        stop()
        return
      }

      loop.scrollTop += props.speed
      if (group.offsetHeight > 0 && loop.scrollTop >= group.offsetHeight) {
        loop.scrollTop -= group.offsetHeight
      }
      animationFrame = window.requestAnimationFrame(tick)
    }

    animationFrame = window.requestAnimationFrame(tick)
  }

  function pause(reason: string) {
    pauseReasons.add(reason)
    stop()
  }

  function resume(reason: string) {
    pauseReasons.delete(reason)
    start()
  }

  function onVisibilityChange() {
    if (document.hidden) stop()
    else start()
  }

  function onReducedMotionChange() {
    if (reducedMotion?.matches) stop()
    else start()
  }

  onMounted(() => {
    reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)')
    reducedMotion.addEventListener('change', onReducedMotionChange)
    document.addEventListener('visibilitychange', onVisibilityChange)

    if ('IntersectionObserver' in window && honorLoop.value) {
      observer = new IntersectionObserver(
        (entries) => {
          isVisible = entries.some((entry) => entry.isIntersecting)
          if (isVisible) start()
          else stop()
        },
        { threshold: 0.15 }
      )
      observer.observe(honorLoop.value)
      return
    }

    isVisible = true
    start()
  })

  onBeforeUnmount(() => {
    stop()
    observer?.disconnect()
    reducedMotion?.removeEventListener('change', onReducedMotionChange)
    document.removeEventListener('visibilitychange', onVisibilityChange)
  })
</script>

<style scoped lang="scss">
  .honor-loop {
    max-height: 320px;
    margin-top: 28px;
    overflow-y: auto;
    overscroll-behavior: contain;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }

    &:focus-visible {
      outline: 2px solid var(--accent);
      outline-offset: 4px;
    }
  }

  .honor-loop-group p {
    padding: 15px 0;
    margin: 0;
    color: var(--fg);
    border-bottom: 1px solid var(--border);

    span {
      margin-right: 9px;
      color: var(--accent);
    }
  }

  @include lt-xs {
    .honor-loop {
      max-height: 290px;
    }
  }
</style>
